import React, {FC, ReactNode, useCallback, useMemo, useState} from 'react';
import dynamic from 'next/dynamic';
import {Modal} from 'modules/modals/components/Modal';
import {MODALS} from 'modules/modals/constants';
import {ModalLazyLoader} from 'components/ModalLazyLoader';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {hideModal, showModal} from 'modules/modals/thunks';
import {IWineShop} from 'modules/wineshops-list/models';
import {PickupModalContext} from 'modules/products/context/PickupModal';
import {ReservationModalContext} from 'modules/product-snippet/context/ReservationModal';
import {useGetProductQuery} from 'modules/product-card/services';
import {skipToken} from '@reduxjs/toolkit/query/react';

const PickUpModal = dynamic(() => import('modules/products/components/PickUpModal'), {
    loading: () => <ModalLazyLoader />,
    ssr: false,
});

const ReserveProductModal = dynamic(() => import('modules/product-card/components/ReserveProductModal'), {
    loading: () => <ModalLazyLoader />,
    ssr: false,
});

interface IProps {
    children: ReactNode;
}

const NOOP_SELECT_SHOP = () => {};

export const ReservationModalProvider: FC<IProps> = ({children}) => {
    const dispatch = useAppDispatch();

    const [wineShopList, setWineShopList] = useState<IWineShop[]>([]);
    const [onSelectShop, setOnSelectShop] = useState<(wineshop: IWineShop) => void>(() => NOOP_SELECT_SHOP);

    const [reserveCode, setReserveCode] = useState<string>();
    const [reserveWineshop, setReserveWineshop] = useState<IWineShop>();

    const {data: reserveProductResponse} = useGetProductQuery(reserveCode ?? skipToken);

    const openWineryPicker = useCallback(
        (list: IWineShop[], handler: (wineshop: IWineShop) => void) => {
            setWineShopList(list);
            setOnSelectShop(() => handler);
            dispatch(showModal(MODALS.SNIPPET_PRODUCT_PICKUP.name));
        },
        [dispatch],
    );

    const openReserveModal = useCallback(
        (code: string, wineshop: IWineShop) => {
            setReserveCode(code);
            setReserveWineshop(wineshop);
            dispatch(showModal(MODALS.SNIPPET_PRODUCT_RESERVE.name));
            dispatch(hideModal(MODALS.SNIPPET_PRODUCT_PICKUP.name));
        },
        [dispatch],
    );

    const closeReserveModal = useCallback(() => {
        dispatch(hideModal(MODALS.SNIPPET_PRODUCT_RESERVE.name));
    }, [dispatch]);

    const pickupContextValue = useMemo(
        () => ({
            isMapButtonShow: true,
            mapButtonTitle: 'Заберу отсюда',
            onSelectShop,
            wineShopList,
            informerText: 'Выберите винотеку, где удобно забрать заказ. Далее обновим каталог по выбранной винотеке',
        }),
        [onSelectShop, wineShopList],
    );

    const reservationContextValue = useMemo(
        () => ({openReserveModal, openWineryPicker}),
        [openReserveModal, openWineryPicker],
    );

    return (
        <ReservationModalContext.Provider value={reservationContextValue}>
            {children}
            <Modal name={MODALS.SNIPPET_PRODUCT_PICKUP.name} willMount={true}>
                <PickupModalContext.Provider value={pickupContextValue}>
                    <PickUpModal />
                </PickupModalContext.Provider>
            </Modal>
            <Modal name={MODALS.SNIPPET_PRODUCT_RESERVE.name} willMount={true} hasPortal={true}>
                <ReserveProductModal
                    onClose={closeReserveModal}
                    productData={reserveProductResponse?.data ?? null}
                    wineshop={reserveWineshop}
                />
            </Modal>
        </ReservationModalContext.Provider>
    );
};
